import pdfMake from 'addthaifont-pdfmake';
import 'addthaifont-pdfmake/build/vfs_fonts';
import buddhistEra from 'dayjs/plugin/buddhistEra';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import dayjs from 'dayjs';

pdfMake.fonts = {
  Sarabun: {
    normal: 'Sarabun-Light.ttf',
    bold: 'Sarabun-Regular.ttf',
    italics: 'Sarabun-LightItalic.ttf',
    bolditalics: 'Sarabun-Italic.ttf',
  },
};

dayjs.locale('th');
dayjs.extend(buddhistEra);
dayjs.extend(LocalizedFormat);

const fmt = (n) => (Number(n) || 0).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');

// รวม employees[] (ต่อวัน) + employee_deductions[] (ต่อคนต่อโครงการ ไม่ผูกวัน) ที่ MergedPayrollTableV2.js
// คำนวณไว้แล้ว ให้เหลือแค่ 1 แถวต่อพนักงาน — ทั้งสอง array มาจากโครงการเดียวกัน (projectRow) เท่านั้น
function aggregateEmployees(projectRow) {
  const totals = {};
  (projectRow?.employees || []).forEach((e) => {
    const id = String(e.employee_id);
    if (!totals[id]) {
      totals[id] = {
        employee_id: id,
        name: e.name || '-',
        salary_per_day: e.salary_per_day || 0,
        amount: 0,
        ot_amount: 0,
        field_allowance_amount: 0,
        allowance: 0,
        bonus: 0,
        sso: 0,
        tax: 0,
        other: 0,
      };
    }
    totals[id].amount += e.amount || 0;
    totals[id].ot_amount += e.ot_amount || 0;
    totals[id].field_allowance_amount += e.field_allowance_amount || 0;
    totals[id].allowance += e.allowance || 0;
  });
  (projectRow?.employee_deductions || []).forEach((d) => {
    const id = String(d.employee_id);
    if (!totals[id]) {
      // เผื่อกรณีมีแค่ bonus/หัก แต่ไม่มี checkin ในโครงการนี้เลย (ไม่ควรเกิดในทางปฏิบัติ แต่กันไว้ไม่ให้ตกหล่น)
      totals[id] = {
        employee_id: id, name: '-', salary_per_day: 0,
        amount: 0, ot_amount: 0, field_allowance_amount: 0, allowance: 0,
        bonus: 0, sso: 0, tax: 0, other: 0,
      };
    }
    totals[id].bonus += d.bonus || 0;
    totals[id].sso += d.sso || 0;
    totals[id].tax += d.tax || 0;
    totals[id].other += d.other || 0;
  });
  return Object.values(totals);
}

const cell = (text, opts = {}) => ({ text, alignment: 'right', ...opts });

const genBody = (rows) => rows.map((r, index) => {
  const net = r.amount + r.ot_amount + r.field_allowance_amount + r.allowance + r.bonus - r.sso - r.tax - r.other;
  return [
    { text: index + 1, alignment: 'center' },
    { text: r.name },
    cell(fmt(r.salary_per_day)),
    cell(fmt(r.amount)),
    cell(fmt(r.ot_amount)),
    cell(fmt(r.field_allowance_amount)),
    cell(fmt(r.allowance)),
    cell(fmt(r.bonus)),
    cell(fmt(r.sso)),
    cell(fmt(r.tax)),
    cell(fmt(r.other)),
    cell(fmt(net), { bold: true }),
  ];
});

// ใบสรุปค่าแรงต่อโครงการ — หลักฐานประกอบ Expense isLabor (แทนบิล/ไฟล์แนบที่ไม่มีจริง)
// projectRow: 1 แถวจาก costResult.rows/projectDashboard ของโครงการนั้น (มี employees[]/employee_deductions[] ติดมาด้วย)
const laborProjectReceiptPDF = ({ code, projectRow, dateStart, dateEnd, empType, me, open = true }) => {
  const rows = aggregateEmployees(projectRow);
  const genItemBody = genBody(rows);

  const sum = (key) => rows.reduce((s, r) => s + (r[key] || 0), 0);
  const totalNet = projectRow?.total_payable ?? (
    sum('amount') + sum('ot_amount') + sum('field_allowance_amount') + sum('allowance')
    + sum('bonus') - sum('sso') - sum('tax') - sum('other')
  );

  const formatDate = (d) => (d ? dayjs(d).format('DD/MM/BBBB') : '-');
  const empTypeLabel = empType === 'FULLTIME' ? 'พนักงานรายเดือน' : 'พนักงานรายวัน';
  const printBy = me?.userData?.firstname || me?.userData?.lastname
    ? `ผู้สั่งพิมพ์: ${me?.userData?.firstname || ''} ${me?.userData?.lastname || ''}`
    : '';

  const documentRef = {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [20, 110, 20, 40],
    defaultStyle: { font: 'Sarabun', fontSize: 9 },
    info: { title: `ใบสรุปค่าแรง ${code || ''}` },
    header: [
      {
        margin: [20, 12],
        table: {
          widths: ['100%'],
          body: [
            [{ text: 'ใบสรุปค่าแรงประจำโครงการ', style: 'headerPage', border: [false, false, false, false] }],
            [{
              text: `เลขที่รายการเบิก: ${code || '-'}    โครงการ: ${projectRow?.project_number || '-'} ${projectRow?.project_name || ''}`,
              border: [false, false, false, false],
              margin: [0, 2, 0, 0],
            }],
            [{
              text: `งวด: ${formatDate(dateStart)} - ${formatDate(dateEnd)}    ประเภทพนักงาน: ${empTypeLabel}`,
              border: [false, false, false, false],
              margin: [0, 2, 0, 0],
            }],
            [{ text: printBy, border: [false, false, false, false], margin: [0, 2, 0, 0] }],
          ],
        },
      },
    ],
    content: [
      {
        table: {
          headerRows: 1,
          widths: ['4%', '15%', '7%', '7%', '7%', '7%', '7%', '7%', '7%', '7%', '7%', '9%'],
          body: [
            [
              { text: 'ลำดับ', fillColor: '#d2d4d2', alignment: 'center', border: [true, true, true, true] },
              { text: 'ชื่อ-นามสกุล', fillColor: '#d2d4d2', alignment: 'center', border: [true, true, true, true] },
              { text: 'ค่าแรงต่อวัน', fillColor: '#d2d4d2', alignment: 'center', border: [true, true, true, true] },
              { text: 'ค่าแรง', fillColor: '#d2d4d2', alignment: 'center', border: [true, true, true, true] },
              { text: 'OT', fillColor: '#d2d4d2', alignment: 'center', border: [true, true, true, true] },
              { text: 'ค่าออกสนาม', fillColor: '#d2d4d2', alignment: 'center', border: [true, true, true, true] },
              { text: 'เบี้ยเลี้ยง', fillColor: '#d2d4d2', alignment: 'center', border: [true, true, true, true] },
              { text: 'เงินได้อื่นๆ', fillColor: '#d2d4d2', alignment: 'center', border: [true, true, true, true] },
              { text: 'หักประกันสังคม', fillColor: '#d2d4d2', alignment: 'center', border: [true, true, true, true] },
              { text: 'หักภาษี', fillColor: '#d2d4d2', alignment: 'center', border: [true, true, true, true] },
              { text: 'หักอื่นๆ', fillColor: '#d2d4d2', alignment: 'center', border: [true, true, true, true] },
              { text: 'รวมต่อคน', fillColor: '#d2d4d2', alignment: 'center', border: [true, true, true, true] },
            ],
            ...genItemBody,
            [
              { text: 'รวมทุกค่าของทุกคน', colSpan: 2, alignment: 'center', bold: true },
              {},
              cell('-'),
              cell(fmt(sum('amount')), { bold: true }),
              cell(fmt(sum('ot_amount')), { bold: true }),
              cell(fmt(sum('field_allowance_amount')), { bold: true }),
              cell(fmt(sum('allowance')), { bold: true }),
              cell(fmt(sum('bonus')), { bold: true }),
              cell(fmt(sum('sso')), { bold: true }),
              cell(fmt(sum('tax')), { bold: true }),
              cell(fmt(sum('other')), { bold: true }),
              cell(fmt(totalNet), { bold: true }),
            ],
          ],
        },
        layout: {
          fillColor: (rowIndex) => (rowIndex === 0 ? '#d2d4d2' : null),
        },
      },
    ],
    footer(currentPage, pageCount) {
      return {
        margin: [20, 0],
        table: {
          widths: ['*', 'auto'],
          body: [[
            { text: `print date ${dayjs().format('DD/MM/BBBB HH:mm:ss')}`, alignment: 'right', fontSize: 8, border: [false, false, false, false] },
            { text: `page ${currentPage} / ${pageCount}`, alignment: 'right', fontSize: 8, border: [false, false, false, false] },
          ]],
        },
        layout: 'noBorders',
      };
    },
    styles: {
      headerPage: { fontSize: 15, bold: true, alignment: 'center' },
    },
  };

  const pdf = pdfMake.createPdf(documentRef);
  if (open) {
    pdf.open();
    return null;
  }
  return new Promise((resolve) => pdf.getBlob((blob) => resolve(blob)));
};

export default laborProjectReceiptPDF;
